import React from 'react';

import { useReducedMotion } from '../../../hooks/useReducedMotion';
import { SORTABLE_ANIM } from '../../../theme/tokens';

/**
 * The drag-to-reorder mechanic of the editor's lists, with no opinion about what
 * a row looks like.
 *
 * It was written once for the reading order and is now shared with the SEO list
 * builders (key takeaways, FAQ, sources) and the Creatives game lists on phones,
 * which is what makes those feel like the same control rather than like a second,
 * nearly-identical one. Everything about appearance stays with the caller: this
 * returns transforms and handle props, and each list renders its own markup and
 * styles.
 *
 * ## How the drag is staged
 *
 * The array is NOT reordered while the finger is down. The row under the pointer
 * is translated by the raw pointer delta with no transition, so it tracks the
 * finger exactly; its neighbours are translated by one vacancy each, WITH a
 * transition, so the gap opens and closes under it as an eased movement rather
 * than a swap. On release the dragged row is given the same easing and sent to
 * the gap, and only once it has arrived is `onMove` called: the committed order
 * then renders with every transform back at zero, which is pixel-for-pixel where
 * the animation left off, so there is no jump at the hand-off.
 *
 * Pointer events rather than HTML5 drag-and-drop, which does not fire on touch
 * at all. The handle is also a focusable button: Arrow Up/Down move the row
 * without a pointer, and commit immediately (there is no gesture to follow).
 * Give that handle `touch-action: none` in its styles, or a vertical drag on a
 * phone scrolls the page instead of moving the row.
 *
 * Under `prefers-reduced-motion` every transition is dropped and the release
 * commits on the spot.
 *
 * ## Press and hold (`longPress`)
 *
 * A long list on a phone also lifts a row when a finger rests anywhere on it for
 * `SORTABLE_ANIM.LONG_PRESS_MS`, so the handle is not the only, small, target.
 * Until then the touch is an ordinary one: moving past a few pixels, or the
 * browser starting a scroll, abandons the press. Once lifted, a non-passive
 * `touchmove` listener on the list keeps the page from scrolling under the drag.
 * It is registered when the list mounts rather than when the drag starts,
 * because a browser decides at `touchstart` whether a gesture can be cancelled.
 *
 * ## Scrolling while dragging (`autoScroll`)
 *
 * Holding the dragged row near the top or bottom of the viewport scrolls the
 * page, faster the closer it gets to the edge, so a row can travel further than
 * one screen. The row's offset counts the scroll as well as the finger, which
 * keeps it under the finger while the list slides beneath it.
 *
 * ## Rows of different heights
 *
 * The geometry is measured from the DOM when the gesture starts rather than
 * assumed, so a list whose rows are not all the same height reorders correctly:
 * an FAQ row grows with the answer textarea the browser lets an author drag
 * taller, and a source row folds to two lines on mobile. For a list that IS
 * uniform this reduces exactly to "half a row of travel tips the row past its
 * neighbour", which is what the reading order always did.
 */

/** Row geometry as it stood when the gesture began. */
type Layout = {
  /** Each row's top, in viewport coordinates. */
  tops: number[];
  /** Each row's height. */
  heights: number[];
  /** The list's row gap, constant by construction (a flex `gap`). */
  gap: number;
};

/** A drag in progress, or easing into place after the finger lifted. */
type Drag = {
  /** Where the row started, i.e. its index in the caller's array. */
  from: number;
  /** Where it would land if released now. */
  to: number;
  layout: Layout;
  /** Pointer position the drag began at. */
  originY: number;
  /** The scroll position the drag began at, so scrolling mid-drag moves the row too. */
  originScroll: number;
  /** How far the row has travelled from where it started. */
  offset: number;
  /** Released: the row is easing into the gap, and the order commits after. */
  settling: boolean;
};

/** A finger resting on a row, not lifted yet. */
type Press = {
  /** The row's key rather than its index: a settle may commit and re-index the list before the hold ends. */
  key: string;
  pointerId: number;
  element: HTMLElement;
  startX: number;
  startY: number;
  lastY: number;
  /** The long-press timer, or 0 for a mouse press, which waits for movement rather than for time. */
  timer: number;
  /** True while a touch is being held toward a drag: past the slop it ABANDONS (the finger is scrolling).
   *  False for a mouse, where past the slop is exactly when the drag STARTS. */
  hold: boolean;
};

export type SortableRowsOptions = {
  /**
   * One stable key per row, in render order — the row's identity, which is what
   * a keyboard move follows to keep the caret on the row that moved. Its length
   * is the row count.
   */
  keys: string[];
  /** Commit a move: the row at `from` lands at `to`. */
  onMove: (from: number, to: number) => void;
  /** Whether the row at `from` may land at `to`. A drop or a key press it refuses leaves the order (and the focus) as it
   *  was. Without it every move is allowed. */
  canMove?: (from: number, to: number) => boolean;
  /** Also lift a row by pressing and holding anywhere on it (spread `rowProps` on each row). */
  longPress?: boolean;
  /**
   * `longPress` only: a selector for the controls in a row that lift it all the same (a mouse drag, a held finger), as
   * the rest of the row does: the button that is the row's own body (a game list's row, which opens the list). A press
   * on any other control belongs to that control.
   */
  holdThrough?: string;
  /**
   * Scroll while the dragged row is held near an edge of the viewport. The insets are the fixed chrome
   * at each edge (a pinned header, a bottom nav bar); the scroll zones start inside them.
   */
  autoScroll?: { top: number; bottom: number };
};

/** Everything a row needs to place itself while a drag is running. */
export type SortableRowState = {
  /** Pixels to translate the row by right now. */
  shift: number;
  /** Whether the row should ease its transform. */
  animated: boolean;
  /** This is the row under the pointer. */
  dragged: boolean;
  /** The position the row would hold if the drag ended now, so numbering stays honest. */
  position: number;
};

type PointerHandler = (event: React.PointerEvent<HTMLElement>) => void;

export type SortableRowsApi = {
  /** Put this on the element that wraps the rows; its children ARE the rows. */
  listRef: React.RefObject<HTMLUListElement>;
  row: (index: number) => SortableRowState;
  /** Spread onto row `index`'s drag handle (a focusable button). */
  handleProps: (index: number) => {
    ref: (node: HTMLButtonElement | null) => void;
    onPointerDown: PointerHandler;
    onPointerMove: PointerHandler;
    onPointerUp: PointerHandler;
    onPointerCancel: PointerHandler;
    onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  };
  /** Spread onto row `index` itself when `longPress` is on: press and hold lifts it. */
  rowProps: (index: number) => {
    onPointerDown: PointerHandler;
    onPointerMove: PointerHandler;
    onPointerUp: PointerHandler;
    onPointerCancel: PointerHandler;
    onContextMenu: (event: React.MouseEvent<HTMLElement>) => void;
  };
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

/** How far a resting finger may wander before the press counts as the start of a scroll instead. */
export const LONG_PRESS_SLOP_PX = 8;
/** Depth of the band inside each edge where holding the row scrolls the page. */
const AUTO_SCROLL_ZONE_PX = 72;
/** Scroll speed at the very edge of that band. */
const AUTO_SCROLL_MAX_PX_PER_S = 900;
/** A press that starts on one of these is the control's, not the row's. */
export const CONTROLS = 'button, a, input, textarea, select, [role="menu"]';

/**
 * How far the dragged row travels to end up at `to`. Landing BELOW where it
 * started means sitting last among the rows it passed, so the distance is
 * measured to that row's bottom edge; landing above is simply to that row's top.
 */
const landingOffset = (layout: Layout, from: number, to: number): number =>
  to > from
    ? layout.tops[to] + layout.heights[to] - layout.heights[from] - layout.tops[from]
    : layout.tops[to] - layout.tops[from];

/** The space a row leaves behind when it is picked up: itself, plus one gap. */
const vacancyOf = (layout: Layout, from: number): number => layout.heights[from] + layout.gap;

/**
 * Where the row would land if released now: the furthest position it has
 * travelled at least HALF the way towards. Half-way between two landings is the
 * moment the two rows have visually swapped places.
 */
const project = (layout: Layout, from: number, offset: number, count: number): number => {
  let to = from;
  if (offset > 0) {
    for (let j = from + 1; j < count; j += 1) {
      if (offset < (landingOffset(layout, from, j) + landingOffset(layout, from, j - 1)) / 2) break;
      to = j;
    }
  } else if (offset < 0) {
    for (let j = from - 1; j >= 0; j -= 1) {
      if (offset > (landingOffset(layout, from, j) + landingOffset(layout, from, j + 1)) / 2) break;
      to = j;
    }
  }
  return to;
};

const measure = (list: HTMLElement): Layout | null => {
  const rows = Array.from(list.children).filter((node): node is HTMLElement => node instanceof HTMLElement);
  if (rows.length < 2) return null;
  const rects = rows.map((row) => row.getBoundingClientRect());
  const tops = rects.map((rect) => rect.top);
  const heights = rects.map((rect) => rect.height);
  const gap = tops[1] - (tops[0] + heights[0]);
  // A zero-height list is one that has not been laid out yet (a hidden panel, a
  // sheet mid-transition): every distance would be zero and the drag would jump.
  if (heights.some((height) => height <= 0)) return null;
  return { tops, heights, gap };
};

/** The element that scrolls `node`, or null when that is the page itself. */
export const scrollParentOf = (node: HTMLElement): HTMLElement | null => {
  for (let el = node.parentElement; el && el !== document.body; el = el.parentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
  }
  return null;
};

export const scrollTopOf = (scroller: HTMLElement | null): number => (scroller ? scroller.scrollTop : window.scrollY);

const scrollByY = (scroller: HTMLElement | null, dy: number): void => {
  // 'instant' so a page-wide `scroll-behavior: smooth` cannot queue up the steps behind the finger.
  if (scroller) scroller.scrollBy({ top: dy, behavior: 'instant' });
  else window.scrollBy({ top: dy, behavior: 'instant' });
};

/** Swallows the click a browser may still send on `row` as the pointer that dragged it lifts: briefly, only in it, and
 *  only before the next press (a click elsewhere, later, or after a new press is the owner's own: a touch drag usually
 *  sends no click, and the tap that follows it must not be eaten). */
const swallowNextClick = (row: HTMLElement): void => {
  let timer = 0;
  const swallow = (click: MouseEvent): void => {
    if (!(click.target instanceof Node) || !row.contains(click.target)) return;
    click.preventDefault();
    click.stopPropagation();
    disarm();
  };
  const disarm = (): void => {
    window.clearTimeout(timer);
    window.removeEventListener('click', swallow, { capture: true });
    window.removeEventListener('pointerdown', disarm, { capture: true });
  };
  window.addEventListener('click', swallow, { capture: true });
  window.addEventListener('pointerdown', disarm, { capture: true });
  timer = window.setTimeout(disarm, 400);
};

export const useSortableRows = ({
  keys,
  onMove,
  canMove,
  longPress = false,
  holdThrough,
  autoScroll,
}: SortableRowsOptions): SortableRowsApi => {
  const reducedMotion = useReducedMotion();
  const listRef = React.useRef<HTMLUListElement>(null);
  /** Handles by row index, so a refocus never has to guess which button is which. */
  const handlesRef = React.useRef<(HTMLButtonElement | null)[]>([]);
  /** The row a keyboard move asked to keep focused, by identity rather than position. */
  const refocusRef = React.useRef<string | null>(null);
  const settleTimer = React.useRef<number | undefined>(undefined);
  /** The move a released row is still easing towards, readable synchronously. */
  const pendingRef = React.useRef<{ from: number; to: number } | null>(null);
  const [drag, setDragState] = React.useState<Drag | null>(null);
  /**
   * The same drag, readable synchronously. The gesture is driven from the ref and
   * never from a state updater: an updater must be pure, and committing the move
   * from inside one made StrictMode's double invocation reorder the list twice.
   */
  const dragRef = React.useRef<Drag | null>(null);
  const pressRef = React.useRef<Press | null>(null);
  /** What scrolls the list during the current drag (null: the page). */
  const scrollerRef = React.useRef<HTMLElement | null>(null);
  /** The pointer's last viewport Y, which the auto-scroll loop keeps following between pointer events. */
  const pointerYRef = React.useRef(0);
  /** The keys as last rendered, for a press that resolves its row after a commit may have re-indexed it. */
  const keysRef = React.useRef(keys);

  const setDrag = React.useCallback((next: Drag | null): void => {
    dragRef.current = next;
    setDragState(next);
  }, []);

  const count = keys.length;
  // The identity of the ORDER, so the refocus effect below runs on a reorder —
  // the row count alone does not change when two rows swap.
  const order = keys.join(' ');

  React.useLayoutEffect(() => {
    keysRef.current = keys;
  }, [keys]);

  const cancelPress = React.useCallback((): void => {
    const press = pressRef.current;
    if (!press) return;
    window.clearTimeout(press.timer);
    pressRef.current = null;
  }, []);

  React.useEffect(
    () => () => {
      window.clearTimeout(settleTimer.current);
      cancelPress();
    },
    [cancelPress],
  );

  // Place the focus a keyboard move asked for, now that the new order has
  // rendered. Only a keyboard move sets the request, so a drag never disturbs
  // whatever the author had focused.
  React.useEffect(() => {
    const wanted = refocusRef.current;
    if (wanted === null) return;
    refocusRef.current = null;
    const at = order.split(' ').indexOf(wanted);
    if (at === -1) return;
    handlesRef.current[at]?.focus();
  }, [order]);

  // Keep a lifted row's touch from scrolling the page. Registered with the list (not at lift time) so the
  // browser treats the whole gesture as cancellable; it only cancels while a drag is actually running. Bound to the
  // list element as it is, checked after every render: a list mounted after its rows were known (a game list's column
  // of lists, drawn once its page is opened) was never bound, and a held row's touch scrolled the page.
  const holdStill = React.useCallback((event: TouchEvent): void => {
    if (dragRef.current && event.cancelable) event.preventDefault();
  }, []);
  const holdingRef = React.useRef<HTMLUListElement | null>(null);
  React.useEffect(() => {
    const list = longPress ? listRef.current : null;
    if (holdingRef.current === list) return;
    holdingRef.current?.removeEventListener('touchmove', holdStill);
    list?.addEventListener('touchmove', holdStill, { passive: false });
    holdingRef.current = list;
  });
  React.useEffect(
    () => () => {
      holdingRef.current?.removeEventListener('touchmove', holdStill);
      holdingRef.current = null;
    },
    [holdStill],
  );

  const allowed = React.useCallback(
    (from: number, to: number): boolean => from !== to && (canMove?.(from, to) ?? true),
    [canMove],
  );
  const move = React.useCallback(
    (from: number, to: number): void => {
      if (allowed(from, to)) onMove(from, to);
    },
    [allowed, onMove],
  );

  /** Lift row `index` under a pointer that is already down. */
  const begin = React.useCallback(
    (index: number, element: HTMLElement, pointerId: number, clientY: number): boolean => {
      const list = listRef.current;
      if (!list) return false;
      const layout = measure(list);
      if (!layout) return false;
      try {
        element.setPointerCapture(pointerId);
      } catch {
        return false; // the pointer lifted in the meantime
      }
      scrollerRef.current = scrollParentOf(list);
      pointerYRef.current = clientY;
      setDrag({
        from: index,
        to: index,
        layout,
        originY: clientY,
        originScroll: scrollTopOf(scrollerRef.current),
        offset: 0,
        settling: false,
      });
      return true;
    },
    [setDrag],
  );

  /** Put the dragged row where the pointer (and the scroll) now say it is. */
  const follow = React.useCallback(
    (clientY: number): void => {
      const current = dragRef.current;
      if (!current || current.settling) return;
      pointerYRef.current = clientY;
      const offset = clientY - current.originY + scrollTopOf(scrollerRef.current) - current.originScroll;
      const to = clamp(project(current.layout, current.from, offset, count), 0, count - 1);
      if (offset === current.offset && to === current.to) return;
      setDrag({ ...current, offset, to });
    },
    [count, setDrag],
  );

  const onPointerDown = React.useCallback(
    (index: number) =>
      (event: React.PointerEvent<HTMLElement>): void => {
        // Grabbing again before the last release has landed: commit that move NOW
        // rather than let this drag cancel its timer and swallow it. This gesture
        // is then dropped on purpose, because `index` was bound to the order as it
        // is about to stop being, and starting from it would move the wrong row.
        if (pendingRef.current) {
          const { from, to } = pendingRef.current;
          pendingRef.current = null;
          window.clearTimeout(settleTimer.current);
          move(from, to);
          setDrag(null);
          return;
        }
        begin(index, event.currentTarget, event.pointerId, event.clientY);
      },
    [begin, move, setDrag],
  );

  const onPointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLElement>): void => follow(event.clientY),
    [follow],
  );

  /**
   * Let go. The row eases from wherever the finger left it into the gap, and the
   * new order is committed only once it is there, so the commit is invisible.
   * `commit: false` (a cancelled pointer) sends it home instead.
   */
  const endDrag = React.useCallback(
    (commit: boolean) =>
      (event: React.PointerEvent<HTMLElement>): void => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        const current = dragRef.current;
        if (!current || current.settling) return;
        const to = commit ? current.to : current.from;
        if (reducedMotion || to === current.from) {
          setDrag(null);
          move(current.from, to);
          return;
        }
        pendingRef.current = { from: current.from, to };
        settleTimer.current = window.setTimeout(() => {
          pendingRef.current = null;
          setDrag(null);
          move(current.from, to);
        }, SORTABLE_ANIM.SETTLE_MS);
        setDrag({ ...current, to, settling: true });
      },
    [move, reducedMotion, setDrag],
  );

  const onKeyDown = React.useCallback(
    (index: number) =>
      (event: React.KeyboardEvent<HTMLButtonElement>): void => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        event.preventDefault();
        const target = clamp(index + (event.key === 'ArrowDown' ? 1 : -1), 0, count - 1);
        // A move the list refuses asks for no focus either: the order stays as it is, so nothing would take the request.
        if (!allowed(index, target)) return;
        // Ask for the focus by row IDENTITY, and let the effect above place it
        // once the new order has rendered. Focusing the target POSITION here
        // instead put the caret on whichever row happened to sit there in the
        // pre-render DOM, so the second press of a run moved a different row and
        // walked the first one straight back.
        refocusRef.current = keys[index] ?? null;
        move(index, target);
      },
    [allowed, count, keys, move],
  );

  // Auto-scroll: while a row is lifted, a frame loop scrolls whenever the pointer sits in an edge zone, and
  // re-places the row after each step (the finger has not moved, the list has).
  const dragging = drag !== null && !drag.settling;
  const insetTop = autoScroll?.top;
  const insetBottom = autoScroll?.bottom;
  React.useEffect(() => {
    if (!dragging || insetTop === undefined || insetBottom === undefined) return undefined;
    let frame = 0;
    let last = 0;
    let carry = 0;
    const tick = (now: number): void => {
      const elapsed = last ? Math.min(now - last, 50) : 0;
      last = now;
      const scroller = scrollerRef.current;
      const box = scroller?.getBoundingClientRect();
      const top = Math.max(box ? box.top : 0, insetTop) + AUTO_SCROLL_ZONE_PX;
      const bottom =
        Math.min(box ? box.bottom : window.innerHeight, window.innerHeight - insetBottom) - AUTO_SCROLL_ZONE_PX;
      const y = pointerYRef.current;
      let speed = 0;
      if (y < top) speed = -clamp((top - y) / AUTO_SCROLL_ZONE_PX, 0, 1);
      else if (y > bottom) speed = clamp((y - bottom) / AUTO_SCROLL_ZONE_PX, 0, 1);
      carry = speed === 0 ? 0 : carry + (speed * AUTO_SCROLL_MAX_PX_PER_S * elapsed) / 1000;
      const step = Math.trunc(carry);
      if (step !== 0) {
        carry -= step;
        const before = scrollTopOf(scroller);
        scrollByY(scroller, step);
        if (scrollTopOf(scroller) !== before) follow(y);
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [dragging, insetTop, insetBottom, follow]);

  const row = React.useCallback(
    (index: number): SortableRowState => {
      if (!drag) return { shift: 0, animated: false, dragged: false, position: index };

      const { from, to, layout, settling, offset } = drag;
      const vacancy = vacancyOf(layout, from);

      let shift = 0;
      if (index === from) shift = settling ? landingOffset(layout, from, to) : offset;
      else if (to > from && index > from && index <= to) shift = -vacancy;
      else if (to < from && index >= to && index < from) shift = vacancy;

      let position = index;
      if (index === from) position = to;
      else if (to > from && index > from && index <= to) position = index - 1;
      else if (to < from && index >= to && index < from) position = index + 1;

      const dragged = index === from;
      return {
        shift,
        /*
         * Two rows never ease: the one under the finger, which is driven straight
         * from the pointer, and EVERY row once the drag is over. At the commit the
         * reorder lands and the transforms snap back to zero in the same paint —
         * the two cancel out exactly, and the row does not move. Leaving the
         * transition on made that reset visible: the row above the drop travelled
         * its pitch a second time, which read as a flicker.
         */
        animated: !reducedMotion && (!dragged || settling),
        dragged,
        position,
      };
    },
    [drag, reducedMotion],
  );

  const handleProps = React.useCallback(
    (index: number) => ({
      ref: (node: HTMLButtonElement | null): void => {
        handlesRef.current[index] = node;
      },
      onPointerDown: onPointerDown(index),
      onPointerMove,
      onPointerUp: endDrag(true),
      onPointerCancel: endDrag(false),
      onKeyDown: onKeyDown(index),
    }),
    [endDrag, onKeyDown, onPointerDown, onPointerMove],
  );

  const rowProps = React.useCallback(
    (index: number) => ({
      onPointerDown: (event: React.PointerEvent<HTMLElement>): void => {
        if (!longPress || dragRef.current) return;
        // Pressing again before the last release has settled: commit that move NOW instead of swallowing
        // the gesture. Back-to-back reorders are normal with a mouse, where the 190ms window is invisible.
        if (pendingRef.current) {
          const { from, to } = pendingRef.current;
          pendingRef.current = null;
          window.clearTimeout(settleTimer.current);
          move(from, to);
          setDrag(null);
          return;
        }
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        // A press that lands on a control in the row (its menu, a button) belongs to that control,
        // whichever pointer it came from — this must be checked BEFORE a mouse starts dragging, or the
        // drag swallows the click. Not one the row lifts through (`holdThrough`): a plain click on it is still
        // its own, and a drag from it ends on the row (which holds the pointer), so the control is not clicked.
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest(CONTROLS) && !(holdThrough && target.closest(holdThrough))) return;
        const key = keys[index];
        if (key === undefined) return;
        cancelPress();
        // A MOUSE drag needs no hold. Waiting for one made the row feel undraggable and sent people back
        // to the handle. It does wait for MOVEMENT: starting on the press alone turned every plain click
        // into a live drag, which inside the list's auto-scroll zone scrolled under the held button and
        // committed a reorder on release. Touch keeps the hold, which is what lets a finger scroll.
        if (event.pointerType === 'mouse') {
          pressRef.current = {
            key,
            pointerId: event.pointerId,
            element: event.currentTarget,
            startX: event.clientX,
            startY: event.clientY,
            lastY: event.clientY,
            timer: 0,
            hold: false,
          };
          return;
        }
        const timer = window.setTimeout(() => {
          const press = pressRef.current;
          pressRef.current = null;
          if (!press || dragRef.current) return;
          const at = keysRef.current.indexOf(press.key);
          if (at === -1) return;
          if (begin(at, press.element, press.pointerId, press.lastY)) navigator.vibrate?.(10);
        }, SORTABLE_ANIM.LONG_PRESS_MS);
        pressRef.current = {
          key,
          pointerId: event.pointerId,
          element: event.currentTarget,
          startX: event.clientX,
          startY: event.clientY,
          lastY: event.clientY,
          timer,
          hold: true,
        };
      },
      onPointerMove: (event: React.PointerEvent<HTMLElement>): void => {
        const press = pressRef.current;
        if (press && press.pointerId === event.pointerId) {
          press.lastY = event.clientY;
          const moved = Math.hypot(event.clientX - press.startX, event.clientY - press.startY);
          if (moved <= LONG_PRESS_SLOP_PX) return;
          const { element, pointerId, hold } = press;
          const at = keysRef.current.indexOf(press.key);
          cancelPress();
          // Past the slop the two pointers part ways: the finger was scrolling, the mouse was dragging.
          if (!hold && at !== -1) begin(at, element, pointerId, event.clientY);
          return;
        }
        onPointerMove(event);
      },
      onPointerUp: (event: React.PointerEvent<HTMLElement>): void => {
        if (pressRef.current) {
          cancelPress();
          return;
        }
        // This pointer's drag, not one still settling from before (a new press then is a click of its own).
        const dropping = dragRef.current !== null && !dragRef.current.settling;
        const row = event.currentTarget;
        endDrag(true)(event);
        // A row lifted from a control it lifts through (`holdThrough`) is dropped, not clicked: a touch may still
        // send the click of its tap to that control as the finger lifts.
        if (dropping) swallowNextClick(row);
      },
      onPointerCancel: (event: React.PointerEvent<HTMLElement>): void => {
        if (pressRef.current) cancelPress();
        else endDrag(false)(event);
      },
      // A held finger opens the context menu on Android; it is the drag's gesture now.
      onContextMenu: (event: React.MouseEvent<HTMLElement>): void => {
        if (pressRef.current || dragRef.current) event.preventDefault();
      },
    }),
    [begin, cancelPress, endDrag, holdThrough, keys, longPress, move, onPointerMove, setDrag],
  );

  return { listRef, row, handleProps, rowProps };
};

export default useSortableRows;
